import React from 'react';
import { X, Edit } from 'lucide-react';
import BpuDetailedView from '../pages/Achat/components/ClientDetail/BpuDetailedView';

interface Client {
    id: number;
    company_name: string;
}

interface ClientPricing {
    id: number;
    reference_number: string;
    client_id: number;
    client_name?: string;
    description: string | null;
    status: 'draft' | 'validated' | 'archived';
    Client?: Client;
}

interface BpuViewModalProps {
    isOpen: boolean;
    onClose: () => void;
    bpuDetails: ClientPricing | null;
    viewingBpu: any;
    buyers: Client[];
    loadingDetails: boolean;
    canEdit?: boolean;
    onEdit?: () => void;
}

const BpuViewModal: React.FC<BpuViewModalProps> = ({
    isOpen,
    onClose,
    bpuDetails,
    viewingBpu,
    buyers,
    loadingDetails,
    canEdit = false,
    onEdit
}) => {
    if (!isOpen || !viewingBpu) return null;

    const clientId: number | null = bpuDetails?.client_id ?? viewingBpu?.client_id ?? null;
    const clientName: string | undefined =
        bpuDetails?.Client?.company_name ||
        bpuDetails?.client_name ||
        viewingBpu?.client_name ||
        viewingBpu?.Client?.company_name ||
        (bpuDetails?.client_id ? buyers.find(b => b.id === bpuDetails.client_id)?.company_name : undefined);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 p-6 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Détails du BPU</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{viewingBpu.reference_number}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {canEdit && onEdit && (
                            <button
                                type="button"
                                onClick={onEdit}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
                                title="Modifier"
                            >
                                <Edit className="h-4 w-4" />
                                Modifier
                            </button>
                        )}
                        <button onClick={onClose} aria-label="Fermer" title="Fermer" className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loadingDetails ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                        </div>
                    ) : bpuDetails ? (
                        <>
                            {/* Informations générales */}
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div>
                                    <span className="text-sm text-slate-500 dark:text-slate-400">Client</span>
                                    <p className="font-medium text-slate-900 dark:text-slate-100">
                                        {clientName || `Client #${bpuDetails.client_id}`}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm text-slate-500 dark:text-slate-400">Statut</span>
                                    <div className="mt-1">
                                        {bpuDetails.status === 'validated' && (
                                            <span className="inline-block px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium rounded-full">
                                                ✓ Validé
                                            </span>
                                        )}
                                        {bpuDetails.status === 'draft' && (
                                            <span className="inline-block px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 text-xs font-medium rounded-full">
                                                ✏️ Brouillon
                                            </span>
                                        )}
                                        {bpuDetails.status === 'archived' && (
                                            <span className="inline-block px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-xs font-medium rounded-full">
                                                🗑️ Supprimé
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {bpuDetails.description && (
                                    <div className="col-span-2">
                                        <span className="text-sm text-slate-500 dark:text-slate-400">Description</span>
                                        <p className="text-sm text-slate-700 dark:text-slate-300">
                                            {bpuDetails.description &&
                                                bpuDetails.description.includes('BPU créé automatiquement lors de la conversion du prospect')
                                                ? `BPU créé automatiquement lors de la conversion du prospect ${bpuDetails.Client?.company_name || viewingBpu?.Client?.company_name || buyers.find(b => b.id === bpuDetails.client_id)?.company_name || ''}`
                                                : bpuDetails.description}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Vue détaillée lecture seule */}
                            <div>
                                {clientId ? (
                                    <BpuDetailedView clientId={clientId} />
                                ) : (
                                    <div className="text-center py-12 text-slate-500">
                                        Aucune donnée disponible
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12 text-slate-500">
                            Aucune donnée disponible
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BpuViewModal;
